import type { AlpineComponent } from 'alpinejs'
import type { SearchCompletedEvent } from './search_form'
import type { UJSResponse } from './modal_form'

type InvoiceType = 'all_invoices' | 'open' | 'reminder' | 'overdue' | 'paid'
type BillingTab = 'invoices' | 'payments'

type InvoicesAndPaymentsComponent = {
  invoiceType: InvoiceType
  tab: BillingTab
  dateInfo: string | undefined
  changeTab: (newTab: BillingTab) => void
}

export const invoicesAndPayments = ({
  invoiceType,
}: {
  invoiceType: InvoiceType
}): AlpineComponent<InvoicesAndPaymentsComponent> => ({
  invoiceType,
  tab: 'invoices',

  get dateInfo() {
    if (this.tab === 'payments') {
      return window.I18n.paid_date_info
    }

    switch (this.invoiceType) {
      case 'overdue':
        return window.I18n.due_date_info
      case 'paid':
        return window.I18n.paid_date_info
      default:
        return window.I18n.invoice_date_info
    }
  },

  init() {
    const form = document.getElementById('invoices-search-form') as HTMLFormElement | undefined
    setTimeout(() => form?.requestSubmit(), 0)
  },

  changeTab(newTab: BillingTab) {
    const form = document.getElementById(`${newTab}-search-form`) as HTMLFormElement
    form.requestSubmit()

    // Wait for the request to finish before switching to the tab in order to avoid rendering an empty table,
    // which would bring reports into view and fetch them as well.
    document.addEventListener('search-completed', (e) => {
      const { detail } = e as SearchCompletedEvent
      if (detail.formId === form.id) {
        this.tab = newTab
      }
    })
  },
})

export const referenceTransactions = (): AlpineComponent<{
  filename: string | null | undefined
  uploadFile: (event: Event) => void
}> => ({
  filename: null,

  init() {
    const form = this.$root as HTMLFormElement
    form.addEventListener('ajax:success', (e) => {
      const { detail } = e as UJSResponse
      const resultContainer = document.querySelector('#reference-transaction-results')
      resultContainer!.innerHTML = detail[2].responseText
    })

    form.addEventListener('ajax:error', (e) => {
      const { detail } = e as UJSResponse
      const errorMsg = detail[2].responseText
      window.flash('alert', errorMsg)
    })
  },

  uploadFile({ target }) {
    const files = (target as HTMLInputElement).files
    this.filename = files ? files[0]?.name : null
  },
})

type ReportType = 'vat_report_invoiced' | 'vat_report_paid' | 'all_invoices' | 'open_invoices' | 'paid_invoices'

export const billingReports = (
  reportType: ReportType
): AlpineComponent<{ reportType: ReportType; dateInfo: string | undefined }> => ({
  reportType,

  get dateInfo() {
    return ['vat_report_paid', 'paid_invoices'].includes(this.reportType)
      ? window.I18n.paid_date_info
      : window.I18n.invoice_date_info
  },

  init() {
    const container = this.$root as HTMLElement

    // Lazy load reports when the container is scrolled into view
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.intersectionRatio === 0) continue

        container.querySelector('form')?.requestSubmit()
        observer.unobserve(container)
      }
    })

    // Start the observer after a small delay to allow time for invoices to be populated
    setTimeout(() => observer.observe(container), 500)
  },
})

export const reportsTable = (): AlpineComponent<unknown> => ({
  init() {
    const dataTableOptions = {
      tableTools: { sRowSelect: 'multi', sSelectedClass: 'row-selected' },
      lengthMenu: [[10, 25, 50, -1]],
      order: [0, 'desc'],
      dom: 'Bfrtip',
      buttons: [
        'csv',
        'copy',
        {
          // Customize the print output
          extend: 'print',
          customize: function (win: Window) {
            $(win.document).find('table').css('font-family', 'Arial, sans-serif')
            $(win.document).find('td, th').css('padding-inline', '10px')
            $(win.document).find('h1').css('text-align', 'center')
          },
        },
      ],
      responsive: false,
      columnDefs: [{ className: 'dt-center', targets: '_all' }],
      retrieve: true,
      bDestroy: true,
    }

    $(this.$root).DataTable(dataTableOptions)
  },
})
